import { useInRecordSection } from 'components/section-edit/SectionEdit';
import { isValid } from 'date-fns';
import { Controller, FieldValues, SetFieldValue, useWatch } from 'react-hook-form';
import { DatePickerField } from '../common/DatePickerField';
import { CommonInputProps } from './InputTypes';
import InputWrapper from './InputWrapper';
import { useMemoizedRules } from './useMemoizedRules';

interface TimeInputProps<TFormData extends FieldValues> extends CommonInputProps<TFormData> {
    readonly?: boolean;
    fullWidth?: boolean;
    maxDate?: Date;
    timeHasErrors: boolean;
    setValue: SetFieldValue<TFormData>;
}

/** A 24-hour `HH:mm` field bound to a `Date`: the picked hours and minutes on the date the value already carries. */
export const TimeInput = <T extends FieldValues>({
    disabled,
    name,
    required,
    label,
    timeHasErrors,
    setValue,
    readonly,
    fullWidth,
    control,
}: TimeInputProps<T>) => {
    const watchedValue = useWatch({ control, name });
    const isEmpty = watchedValue === undefined || watchedValue === null;
    const rules = useMemoizedRules({ required });
    // In a record section the field fills its cell, as its read value's box does.
    const inSection = useInRecordSection();

    // A time still being typed arrives as an invalid date; the form keeps the last complete one. An emptied field
    // stores null, which the required rule then rejects.
    const handleTimeChange = (value: Date | null) => {
        if (value === null || isValid(value)) {
            setValue(name, value, { shouldValidate: true });
        }
    };

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            render={({ field: { ref, value }, fieldState: { error } }) => (
                <InputWrapper
                    label={label}
                    name={name}
                    hasErrors={timeHasErrors || !!error}
                    error={error}
                    required={required}
                    isEmpty={isEmpty}
                    readonly={readonly}
                >
                    <DatePickerField
                        mode="time"
                        format="HH:mm"
                        id={name}
                        inputRef={ref}
                        value={value || null}
                        onChange={handleTimeChange}
                        disabled={disabled}
                        actions={['accept']}
                        clearable={false}
                        className={fullWidth || inSection ? undefined : 'w-70'}
                    />
                </InputWrapper>
            )}
        />
    );
};
